/** @jsx jsx */

/** @jsxFrag React.Fragment */
import type { SerializedStyles } from '@emotion/react';
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Typography2 } from '@coursera/cds-core';
import { CheckIcon } from '@coursera/cds-icons';

import type {
  BlockContent,
  ReasonType,
} from 'bundles/coursera-plus-landing-page/components/priceComparison/PriceComparisonCopies';
import type { ProductPrices } from 'bundles/payments-common/hooks/useGetProductPrices';

import _t from 'i18n!nls/coursera-plus-landing-page';

const styles = {
  top: css`
    height: 24px;
  `,
  mainTop: css`
    background-color: var(--cds-color-interactive-primary);
    text-align: center;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
  `,
  contentBlock: css`
    text-align: center;
    background-color: white;
    padding-top: var(--cds-spacing-400);
    padding-bottom: var(--cds-spacing-200);
  `,
  mainContentBlock: css`
    border: 3px solid var(--cds-color-interactive-primary);
  `,
  nonMainContentBlock: css`
    margin-top: 3px;
  `,
  titlesBlock: css`
    height: 130px;
  `,
  title: css`
    font-weight: 700 !important;
    font-size: 20px !important;
    line-height: 24px;
    margin-bottom: 10px;
  `,
  subtitle: css`
    font-weight: 400;
    font-size: 16px;
    line-height: 28px;
    padding: 0 20px;
  `,
  extraSpaceOverTitle: css`
    height: 20px;
  `,
  price: css`
    text-align: center;
  `,
  control: (inAnnualPricePresentationVariant: boolean) => css`
    height: ${inAnnualPricePresentationVariant ? '300px' : '210px'};
  `,
  reasonBlock: css`
    margin: 0 var(--cds-spacing-300);
    padding: 20px 0 15px 0;
    border-top-style: solid;
    border-color: var(--cds-color-neutral-disabled);
    border-width: 1px;
    text-align: left;
    line-height: 28px;
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
  `,
  reasonBlockWithIntro: css`
    :first-child {
      border: none;
      padding-top: 0;
    }
  `,
  reasonReasonBlock: css`
    margin-left: var(--cds-spacing-150);
  `,
  reasonList: css`
    list-style-type: none;
    margin: 0;
    padding: 0;
  `,
  actionComment: css`
    margin-top: 20px;
    font-weight: 600;
  `,
};

type PropsToWrapper = {
  children: React.ReactNode;
  isMain?: boolean;
  isActive?: boolean;
};

type PropsToComponent = {
  isMain?: boolean;
  isActive?: boolean;
  content: BlockContent;
  productPrice?: ProductPrices | ProductPrices[];
  isInCourseraPlusLandingPageAnnualPricePresentationVariant?: boolean;
};

type PropsToReasonComponent = {
  reason: ReasonType;
  hasReasonIntro: boolean;
};

const Reason = ({ reason, hasReasonIntro }: PropsToReasonComponent) => {
  const stylesForReasonBlock: (false | SerializedStyles)[] = [];
  stylesForReasonBlock.push(styles.reasonBlock);
  if (hasReasonIntro) stylesForReasonBlock.push(styles.reasonBlockWithIntro);
  return (
    <li css={stylesForReasonBlock}>
      <span>
        <CheckIcon size="large" color="interactive" />
      </span>
      <Typography2 variant="bodyPrimary" component="p" css={styles.reasonReasonBlock}>
        {reason.reason}
      </Typography2>
    </li>
  );
};

const PriceComparisonBlockWrapper = ({ children, isMain, isActive }: PropsToWrapper) => {
  const isMainOrActive = isMain || isActive;

  return (
    <>
      {isMain ? (
        <Typography2 component="div" variant="bodySecondary" css={[styles.top, styles.mainTop]} color="invertBody">
          {_t('Most popular')}
        </Typography2>
      ) : (
        <div css={styles.top} />
      )}
      <div css={[styles.contentBlock, isMainOrActive ? styles.mainContentBlock : styles.nonMainContentBlock]}>
        {children}
      </div>
    </>
  );
};

const PriceComparisonBlock: React.LegacyFunctionComponentWithChildren<PropsToComponent> = (props) => {
  const {
    isMain,
    content,
    productPrice,
    isActive,
    isInCourseraPlusLandingPageAnnualPricePresentationVariant = false,
  } = props;
  if (productPrice === null) {
    return null;
  }
  const { title, subtitle, reasonIntro, reasons, actionComment } = content;
  let reasonKey = 1;
  return (
    <div>
      <PriceComparisonBlockWrapper isMain={isMain} isActive={isActive}>
        <div css={styles.titlesBlock}>
          {!isMain && <div css={styles.extraSpaceOverTitle} />}
          <Typography2 component="h3" css={styles.title}>
            {title}
          </Typography2>
          <div css={styles.subtitle}>
            <Typography2 component="p">{subtitle}</Typography2>
          </div>
        </div>
        <div css={styles.control(isInCourseraPlusLandingPageAnnualPricePresentationVariant)}>
          {props.children}
          {actionComment && (
            <Typography2 component="div" css={styles.actionComment}>
              {actionComment}
            </Typography2>
          )}
        </div>
        {reasonIntro && (
          <Typography2 component="div" css={styles.reasonBlock}>
            {reasonIntro}
          </Typography2>
        )}
        <ul css={styles.reasonList}>
          {reasons.map((reason: ReasonType) => {
            reasonKey += 1;
            return <Reason reason={reason} key={reasonKey} hasReasonIntro={Boolean(reasonIntro)} />;
          })}
        </ul>
      </PriceComparisonBlockWrapper>
    </div>
  );
};

export default PriceComparisonBlock;
